import React, { useEffect } from 'react';
import scrollToHash from 'react-scale/lib/scroll-to-hash';

import Footer from 'react-scale/components/ui/footer';
import Header from 'react-scale/components/ui/header';
import Hero from 'react-scale/components/pages/homepage/hero';
import Partners from 'components/pages/homepage/partners';
import PreFooter from 'react-scale/components/pages/homepage/pre-footer';
import Quote from 'react-scale/components/ui/quote';
import ContactBanner from 'react-scale/components/pages/homepage/contact-banner';
// import UseCases from 'react-scale/components/pages/homepage/use-cases';
import RapidBanner from 'react-scale/components/pages/homepage/rapid-banner';
// import { BrandPanelWithScreenshot } from 'react-scale/components';
import { CTASimpleCentered, DataSlider, Section } from 'react-scale/components';

import dynamic from 'next/dynamic';

const DataCentric = dynamic(() => import('components/pages/homepage/data-centric'), {
  ssr: false
});

const Homepage = () => {
  useEffect(() => {
    if (window.location.hash) scrollToHash(window.location.hash);
  }, []);

  return (
    <div className="overflow-hidden text-white bg-black">
      <Header canonical="https://scale.com/" theme="dark" />
      <Hero />
      <Partners limit={16} />
      <DataCentric />
      <Section id="Annotate">
        <DataSlider
          icon={{
            src: '/static/img/index/data-centric/annotate.png',
            w: 64,
            h: 64
          }}
          overtitle="Annotate Content &amp; Language"
          title="Gather Human Insight"
          description="Retrieve human insights for search relevance, ecommerce, natural language processing, audio transcription, document processing and more. Operational excellence augmented by technology enables us to exceed demanding quality, cost, and latency requirements."
          link={{ href: '/content-language', name: 'Learn More' }}
          images={[
            {
              src: '/static/img/index/examples/content-and-language/named-entity-recognition.svg',
              epigraph: 'Named Entity Recognition'
            },
            {
              src: '/static/img/index/examples/content-and-language/cataloging.png',
              epigraph: 'Cataloging'
            },
            {
              src: '/static/img/index/examples/content-and-language/speech-and-audio.svg',
              epigraph: 'Speech and Audio'
            }
          ]}
        />
      </Section>
      <Section paddingTop={false} paddingBottom={false}>
        <DataSlider
          icon={{
            src: '/static/img/index/data-centric/annotate.png',
            w: 64,
            h: 64
          }}
          overtitle="Annotate Computer Vision"
          title="Scale Advanced Annotations"
          description={`Annotate large volumes of <a class="text-malibu" href="/3d-sensor-fusion">3D sensor</a>, <a class="text-malibu" href="/image">image</a>, and <a class="text-malibu" href="/video">video</a> data at high throughput. ML-powered pre-labeling and an automated quality assurance system ensure high quality annotations for the most safety critical applications.`}
          link={{ href: '/sales', name: 'Contact Sales' }}
          images={[
            {
              src: 'https://lidar-now.scale.com/examples/semseg',
              placeholderSrc: '/static/img/index/examples/computer-vision/lidar-placeholder.png',
              epigraph: '3D Semantic Segmentation',
              isIframe: true
            },
            {
              src: '/static/img/index/examples/computer-vision/video-polygon.png',
              epigraph: 'Video Polygon'
            },
            {
              src: '/static/img/index/examples/computer-vision/image.png',
              epigraph: 'Image'
            }
          ]}
          inverted
        />
      </Section>
      <div className="container my-12 e md:pt-20 md:mt-24">
        <Quote quotes={['toyota', 'statesTitle', 'skydio', 'standardCognition', 'nuro']} />
      </div>

      <Section id="Manage" paddingBottom={false}>
        <DataSlider
          icon={{
            src: '/static/img/index/data-centric/manage.png',
            w: 64,
            h: 64
          }}
          overtitle="Manage"
          title="Manage Your Datasets"
          description={`Quickly choose what data to label with active learning and advanced querying. Visualize data, identify edge cases with integrated model predictions, and solve the long tail with <a class="text-malibu" href="/nucleus">Scale Nucleus</a>.`}
          link={{ href: '/nucleus', name: 'Explore Scale Nucleus' }}
          images={[
            {
              src: '/static/img/index/examples/manage/search-visualization.svg',
              epigraph: 'Search Visualization'
            },
            {
              src: '/static/img/index/examples/manage/visual-similarity-search.svg',
              epigraph: 'Visual Similarity Search'
            }
          ]}
          bleeds
        />
      </Section>
      <Section id="Automate" paddingBottom={false}>
        <DataSlider
          icon={{
            src: '/static/img/index/data-centric/automate.png',
            w: 64,
            h: 64
          }}
          overtitle="Automate"
          title="Automate Document Processing"
          description={`Achieve robust document understanding and extraction across any document type. Pre-trained but fine-tuned with your data to your exact use case, <a class="text-malibu" href="/document-ai">Scale Document AI</a> guarantees 99%+ quality and low latency to reduce costs up to 90%+ with an optional human-in-the-loop review.`}
          link={{ href: '/document-ai', name: 'Learn How' }}
          images={[
            {
              src: '/static/img/index/examples/automate/invoice-extraction-model.svg',
              epigraph: 'Invoice Extraction Model'
            },
            {
              src: '/static/img/index/examples/automate/bill-of-lading-extraction-model.svg',
              epigraph: 'Bill of Lading Extraction Model'
            },
            {
              src: '/static/img/index/examples/automate/pathology-extraction-model.svg',
              epigraph: 'Pathology Extraction Model'
            }
          ]}
          inverted
          bleeds
        />
      </Section>

      <ContactBanner />
      <Section id="Evaluate" paddingBottom={false}>
        <DataSlider
          icon={{
            src: '/static/img/index/data-centric/evaluate.png',
            w: 64,
            h: 64
          }}
          overtitle="Evaluate"
          title="Test, Validate &amp; Debug Models"
          description="Upload predictions to Nucleus via API. Track model performance, compare model runs, sort failure examples by metrics of interest, and build model unit tests out of curated dataset slices to catch regressions in key scenarios."
          link={{ href: '/nucleus', name: 'Learn How' }}
          images={[
            {
              src: '/static/img/index/examples/evaluate/model-performance.svg',
              epigraph: 'Model Performance'
            },
            {
              src: '/static/img/index/examples/evaluate/model-validation.svg',
              epigraph: 'Model Validation'
            },
            {
              src: '/static/img/index/examples/evaluate/model-debugging.svg',
              epigraph: 'Model Debugging'
            }
          ]}
          bleeds
        />
      </Section>
      <Section id="Collect">
        <DataSlider
          icon={{
            src: '/static/img/index/data-centric/collect.png',
            w: 64,
            h: 64
          }}
          overtitle="Collect"
          title="Collect Diverse Data"
          description="Collect and generate representative device, image, video, text and audio data in 50+ languages across 70+ countries. Data collection workflows are API supported and seamlessly integrate with Scale's data labeling pipeline."
          cta={{
            hubspotForm: {
              id: '1d163103-bd52-43cb-8fe1-8da65276d3db',
              label: 'Get on the Waitlist',
              duplicateId: 4
            }
          }}
          images={[
            {
              src: '/static/img/index/examples/collect/audio-collection.svg',
              epigraph: 'Audio Collection'
            },
            {
              src: '/static/img/index/examples/collect/text-collection.svg',
              epigraph: 'Text Collection'
            }
          ]}
          inverted
        />
      </Section>
      <Section id="Generate" paddingTop={false} paddingBottom={false}>
        <DataSlider
          icon={{
            src: '/static/img/index/data-centric/generate.png',
            w: 64,
            h: 64
          }}
          overtitle="Generate"
          title="Generate Synthetic Data"
          description="Augment ground-truth training data with infinite varieties of synthetic data and expose your model to more data than you can otherwise collect. Confidently develop generalizable ML models by understanding how they will react to rare or dangerous real-world scenarios before you encounter them in production."
          images={[
            {
              src: '/static/img/index/examples/generate/synthetic-data.png',
              epigraph: 'Synthetic Data'
            }
          ]}
          link={{ href: '/synthetic', name: 'Learn More' }}
        />
      </Section>
      <Section paddingTop={false} container={false}>
        <RapidBanner />
      </Section>
      {/* <BrandPanelWithScreenshot inverted img="/static/images/pages/homepage/rapid-banner.png" /> */}
      {/* <UseCases /> */}
      <PreFooter />
      <CTASimpleCentered border />
      <Footer border />
    </div>
  );
};

export default Homepage;
